import React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import { Button } from '../ui/Button'
import { FloatingParticles, GradientOrbs } from '../animations/FloatingParticles'
import { StaggeredFadeInUp } from '../animations/FadeInUp'
import { ResponsiveContainer } from '../layout/ResponsiveContainer'
import { usePrefersReducedMotion } from '../../hooks/useResponsive'

interface HeroSectionProps {
  title: string
  subtitle: string
  description?: string
  primaryCTA: {
    text: string
    onClick: () => void
  }
  secondaryCTA?: {
    text: string
    onClick: () => void
  }
  backgroundImage?: string
  className?: string
}

export function HeroSection({
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  backgroundImage,
  className,
}: HeroSectionProps) {
  const prefersReducedMotion = usePrefersReducedMotion()

  return (
    <section
      className={cn(
        'relative min-h-screen flex items-center justify-center',
        'bg-gradient-to-br from-gray-50 via-white to-gray-100',
        'overflow-hidden',
        className
      )}
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        {backgroundImage && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )}
        <GradientOrbs />
        <FloatingParticles count={15} />
      </div>

      {/* Content */}
      <ResponsiveContainer className="relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <StaggeredFadeInUp
            staggerDelay={0.2}
            className="space-y-8"
          >
            {/* Subtitle */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: prefersReducedMotion ? 0.01 : 0.6,
                delay: prefersReducedMotion ? 0 : 0.1,
                ease: 'easeOut',
              }}
            >
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-100 text-primary-800 mb-6">
                {subtitle}
              </span>
            </motion.div>

            {/* Main Title */}
            <div>
              <h1 className="text-hero font-bold text-gray-900 leading-tight">
                {title}
              </h1>
            </div>

            {/* Description */}
            {description && (
              <div>
                <p className="text-body-large text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  {description}
                </p>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <motion.div
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                <Button
                  size="xl"
                  onClick={primaryCTA.onClick}
                  className="w-full sm:w-auto shadow-lg hover:shadow-xl"
                >
                  {primaryCTA.text}
                </Button>
              </motion.div>

              {secondaryCTA && (
                <motion.div
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                >
                  <Button
                    variant="outline"
                    size="xl"
                    onClick={secondaryCTA.onClick}
                    className="w-full sm:w-auto"
                  >
                    {secondaryCTA.text}
                  </Button>
                </motion.div>
              )}
            </div>
          </StaggeredFadeInUp>
        </div>
      </ResponsiveContainer>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={prefersReducedMotion ? {} : { y: [0, 10, 0] }}
        transition={prefersReducedMotion ? {} : {
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <div className="flex flex-col items-center" style={{ color: '#1A1A1A' }}>
          <span className="text-sm mb-2" style={{ fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }}>Scroll to explore</span>
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ opacity: 0.7 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </motion.div>
    </section>
  )
}

// Specialized hero for service pages
interface ServiceHeroProps extends Omit<HeroSectionProps, 'className'> {
  serviceType: 'hr' | 'compliance' | 'trade' | 'intelligence'
  icon?: React.ReactNode
  className?: string
}

export function ServiceHero({
  serviceType,
  title,
  subtitle,
  description,
  primaryCTA,
  secondaryCTA,
  className,
}: ServiceHeroProps) {
  const serviceGradients = {
    hr: 'from-hr-50 via-white to-hr-100',
    compliance: 'from-compliance-50 via-white to-compliance-100',
    trade: 'from-trade-50 via-white to-trade-100',
    intelligence: 'from-intelligence-50 via-white to-intelligence-100',
  }

  return (
    <HeroSection
      title={title}
      subtitle={subtitle}
      description={description}
      primaryCTA={primaryCTA}
      secondaryCTA={secondaryCTA}
      className={cn(
        `bg-gradient-to-br ${serviceGradients[serviceType]}`,
        className
      )}
    />
  )
}